// Month-over-month headcount. Each point is ONE snapshot — the last committed
// for that month — never a sum of the uploads in it: headcount is a level, not
// a flow, the same reasoning InventoryTrend applies to a stock position.
//
// Deliberately quiet until it has something to say. One month is a dot and no
// trend at all, so it says so rather than drawing a line through a single
// point and inviting someone to read a direction into it.

import { useQuery } from '@tanstack/react-query';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip,
} from 'recharts';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { api } from '../../services/api';
import { c, t, r, card, chart, tbl } from '../../theme';
import LoadingSpinner from '../Shared/LoadingSpinner';

const monthLabel = (iso) =>
  new Date(`${String(iso).slice(0, 10)}T00:00:00`).toLocaleDateString(undefined, { month: 'short', year: '2-digit' });

export default function HeadcountTrend({ ccId, ccName, onSelectCostCenter }) {
  const trend = useQuery({
    queryKey: ['hr-trend', ccId || 'all'],
    queryFn: () => api.get('/hr-headcount/trend', { params: ccId ? { cc_id: ccId } : {} }),
  });
  const movers = useQuery({
    queryKey: ['hr-trend-movers'],
    queryFn: () => api.get('/hr-headcount/trend/by-cost-center'),
    enabled: !ccId,
  });

  if (trend.isLoading) return <div style={{ ...card.base, padding: 20 }}><LoadingSpinner /></div>;
  if (trend.error) return null;

  const months = trend.data?.months || [];

  if (months.length === 0) return null;

  if (months.length === 1) {
    return (
      <div style={{ ...card.base, padding: '14px 18px', marginBottom: 16 }}>
        <div style={{ ...t.bodySm, color: c.textMuted }}>
          One snapshot so far ({monthLabel(months[0].month)}, {months[0].heads.toLocaleString()} heads).
          A trend appears once a second month is uploaded.
        </div>
      </div>
    );
  }

  const data = months.map((m) => ({
    month: monthLabel(m.month),
    heads: m.heads,
    change: m.change,
    provisional: m.is_provisional,
  }));
  const latest = months[months.length - 1];
  const net = latest.change;

  return (
    <div style={{ ...card.base, padding: '18px 20px', marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 12 }}>
        <div>
          <div style={{ ...t.label, color: c.textMuted }}>
            Headcount by month{ccName ? ` — ${ccName}` : ''}
          </div>
          <div style={{ ...t.bodySm, color: c.textFaint }}>
            One point per month — the last snapshot committed for it
          </div>
        </div>
        <Delta value={net} />
      </div>

      <ResponsiveContainer width="100%" height={200}>
        <ComposedChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid stroke={chart.grid} vertical={false} />
          <XAxis dataKey="month" tick={{ fill: chart.axisText, fontSize: 11 }} tickLine={false} axisLine={false} />
          <YAxis tick={{ fill: chart.axisText, fontSize: 11 }} tickLine={false} axisLine={false} width={44} />
          <Tooltip
            contentStyle={{ border: `1px solid ${c.border}`, borderRadius: r.md, fontSize: 12 }}
            formatter={(v, n) => [Number(v).toLocaleString(), n === 'heads' ? 'Heads' : n]}
          />
          <Bar dataKey="heads" fill={chart.sequential[0]} radius={[3, 3, 0, 0]} maxBarSize={54} />
          <Line type="monotone" dataKey="heads" stroke={chart.emphasis} strokeWidth={2} dot={{ r: 3 }} />
        </ComposedChart>
      </ResponsiveContainer>

      {months.some((m) => m.is_provisional) && (
        <div style={{ ...t.micro, color: c.warningText, marginTop: 6 }}>
          One or more months are marked provisional — those extracts were known to be incomplete.
        </div>
      )}

      {!ccId && movers.data?.cost_centers?.length > 0 && (
        <Movers rows={movers.data.cost_centers} from={movers.data.from} to={movers.data.to}
                onSelect={onSelectCostCenter} />
      )}
    </div>
  );
}

function Delta({ value }) {
  if (value === null || value === undefined) return null;
  const [Icon, tone] = value > 0 ? [TrendingUp, c.successText]
    : value < 0 ? [TrendingDown, c.dangerText]
    : [Minus, c.textMuted];
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 5, color: tone, ...t.bodySm }}>
      <Icon size={15} />
      {value > 0 ? '+' : ''}{value.toLocaleString()} vs previous month
    </div>
  );
}

// Only the cost centres that actually moved. A table of fifty zeroes is a way
// of hiding the four numbers someone came here for.
function Movers({ rows, from, to, onSelect }) {
  const moved = rows.filter((x) => x.change !== 0);
  if (!moved.length) {
    return (
      <div style={{ ...t.bodySm, color: c.textMuted, marginTop: 14 }}>
        No cost center changed between {monthLabel(from)} and {monthLabel(to)}.
      </div>
    );
  }
  const up = moved.filter((x) => x.change > 0);
  const down = moved.filter((x) => x.change < 0).reverse();
  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ ...t.label, color: c.textStrong, marginBottom: 6 }}>
        Movement {monthLabel(from)} → {monthLabel(to)}
        <span style={{ ...t.micro, color: c.textFaint, marginLeft: 8, fontWeight: 400 }}>
          {moved.length} of {rows.length} cost centers changed{onSelect ? ' — click one to open its history' : ''}
        </span>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ background: c.surfaceSunk }}>
            <th style={tbl.thSm}>Cost Center</th>
            <th style={tbl.thRSm}>Was</th>
            <th style={tbl.thRSm}>Now</th>
            <th style={tbl.thRSm}>Change</th>
          </tr>
        </thead>
        <tbody>
          {[...up, ...down].map((x) => (
            <tr key={x.raw_cost_center}
                style={onSelect && x.cost_center_id ? tbl.trHover : tbl.tr}
                onClick={() => onSelect && x.cost_center_id && onSelect(x.cost_center_id)}>
              <td style={tbl.tdSm}>{x.raw_cost_center}</td>
              <td style={{ ...tbl.tdRSm, color: c.textMuted }}>{x.previous}</td>
              <td style={tbl.tdRSm}>{x.current}</td>
              <td style={{ ...tbl.tdRSm, color: x.change > 0 ? c.successText : c.dangerText, fontWeight: 600 }}>
                {x.change > 0 ? '+' : ''}{x.change}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
